import type { ComponentPropsWithoutRef } from 'react';

import { clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';

// The root wraps the content in a solid `bg-background` underlay so the
// translucent variant surfaces (`bg-warning/15`, `bg-primary/20`, …) composite
// over the app background consistently rather than over whatever sits behind
// the badge. Its corner radius must track the content's `rounded`.
const badgeRootVariants = cva('bg-background w-max', {
  variants: {
    rounded: {
      none: 'rounded-none',
      base: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    },
  },
  defaultVariants: { rounded: 'base' },
});

const badgeVariants = cva('flex w-fit items-center gap-2 text-xs/[20px] transition', {
  variants: {
    variant: {
      base: 'bg-background text-foreground',
      warning: 'bg-warning/15 text-warning',
      inverted: 'bg-background-inverted text-foreground-inverted/60 ring-foreground-inverted/60',
      'inverted-muted':
        'bg-background-inverted text-foreground-inverted/60 ring-foreground-inverted/20',
      primary: 'bg-primary text-primary-foreground',
      'primary-light': 'bg-primary/20 text-primary-intense',
      'primary-ghost': 'text-primary font-bold',
      warningIntense: 'bg-warning-intense text-warning-intense-foreground',
      neutral: 'bg-neutral text-neutral-foreground',
      success: 'bg-success text-background',
      destructive: 'bg-destructive/15 text-destructive-intense',
      info: 'bg-info/20 text-info',
      gold: 'bg-gold text-foreground',
      silver: 'bg-silver text-foreground',
      bronze: 'bg-bronze text-foreground',
    },
    size: {
      none: '',
      sm: 'px-3 py-1',
      base: 'tab:px-4 tab:py-2 px-3 py-1',
      lg: 'tab:px-5 tab:py-2 px-4 py-1.5',
    },
    outline: {
      true: 'ring-1',
    },
    rounded: {
      none: 'rounded-none',
      base: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    },
    hover: {
      light: 'hover:brightness-125',
      dark: 'hover:brightness-75',
    },
  },
  compoundVariants: [
    {
      variant: [
        'warning',
        'primary',
        'primary-light',
        'warningIntense',
        'neutral',
        'primary-ghost',
        'success',
        'destructive',
        'info',
      ],
      className: 'tracking-extra-wide uppercase',
    },
    {
      variant: ['base', 'inverted', 'inverted-muted'],
      className: 'tracking-tight',
    },
  ],
  defaultVariants: {
    variant: 'base',
    size: 'base',
    rounded: 'base',
  },
});

export type BadgeProps = ComponentPropsWithoutRef<'span'> & VariantProps<typeof badgeVariants>;

export function Badge({
  variant,
  size,
  outline,
  rounded = 'base',
  hover,
  className,
  ...props
}: BadgeProps) {
  return (
    <span data-slot="badge" className={clsxm(badgeRootVariants({ rounded }))}>
      <span
        data-slot="badge-content"
        {...props}
        className={clsxm(badgeVariants({ variant, size, outline, rounded, hover }), className)}
      />
    </span>
  );
}

export { badgeVariants };
